import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Paleta de colores corporativos
const COLORES = {
    NARANJA: '#fe7f2d',
    VERDE: '#297373',
    MORADO: '#6b3fa0',
    AZUL: '#0ea5e9',
    GRIS_CLARO: '#f8f9fa',
    GRIS_MEDIO: '#e9ecef',
    TEXTO: '#212529',
    TEXTO_SUAVE: '#6c757d'
};

// Helpers de color
const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
    ] : [0, 0, 0];
};

// Descripciones por nivel
const DESCRIPCIONES_NIVEL = {
    SUPERIOR: `Se encuentra en nivel Superior. Presenta facilidad para analizar, comprender y seguir nuevas instrucciones, empleando su capacidad de razonamiento. Cuenta con facultad para la adquisición de nuevos conocimientos en áreas afines, permitiéndose desarrollar sus funcionalidades y aportar soluciones en su área de trabajo. Utiliza lenguaje fluido y coherente, acorde a sus competencias. Su nivel de atención, memoria y concentración se encuentran conservadas.`,

    NORMAL_SUPERIOR: `Presenta facilidades para la comprensión y razonamiento de ideas y tareas expresadas mediante palabras. Muestra agilidad para el reconocimiento y asociación de conceptos verbales. Así mismo emplea eficazmente lo aprendido previamente. Mantiene una adecuada capacidad almacenar y evocar información pasada en corto tiempo. Su nivel de atención, memoria y concentración se encuentran conservadas. Utiliza un lenguaje fluido y coherente.`,

    NORMAL_PROMEDIO: `Se encuentra en nivel Normal Promedio. Presenta facilidad para evocar rápidamente los conocimientos aprendidos. Muestra una adecuada capacidad para comprender ideas expresadas en forma escrita o hablado, lo que le permite seguir instrucciones fácilmente. Así mismo, presenta un adecuado nivel de análisis para la resolución de problemas. Su nivel de memoria, atención y concentración se encuentran conservadas. Utiliza lenguajes fluido y coherente en su curso y contenido.`,

    INFERIOR: `Se encuentra en nivel Inferior, su capacidad le permite comprender y seguir instrucciones sencillas. Presenta capacidad para adquirir nuevos conocimientos y basarse en su experiencia. Se muestra con un lenguaje fluido y comprensible, acorde al contexto, no se evidencia alteraciones. Su memoria, atención y concentración se encuentran conservadas. Se encuentra ubicad@ en tiempo y espacio.`
};

const obtenerColorNivel = (nivel) => {
    const colores = {
        SUPERIOR: '#9333ea',
        NORMAL_SUPERIOR: '#3b82f6',
        NORMAL_PROMEDIO: '#f59e0b',
        INFERIOR: '#ef4444'
    };
    return colores[nivel] || '#6b7280';
};

const obtenerTextoNivel = (nivel) => {
    const textos = {
        SUPERIOR: 'Intelectualmente Superior',
        NORMAL_SUPERIOR: 'Normal Superior',
        NORMAL_PROMEDIO: 'Normal Promedio',
        INFERIOR: 'Inferior'
    };
    return textos[nivel] || nivel;
};

const obtenerGradoNivel = (porcentaje) => {
    if (porcentaje >= 95) return { grado: 'Grado I', nivel: 'SUPERIOR', rango: '95 +' };
    if (porcentaje >= 75) return { grado: 'Grado II', nivel: 'NORMAL_SUPERIOR', rango: '75 - 94' };
    if (porcentaje >= 50) return { grado: 'Grado III', nivel: 'NORMAL_SUPERIOR', rango: '50 - 74' };
    if (porcentaje >= 25) return { grado: 'Grado III', nivel: 'NORMAL_PROMEDIO', rango: '25 - 49' };
    if (porcentaje >= 5) return { grado: 'Grado IV', nivel: 'INFERIOR', rango: '5 - 24' };
    return { grado: 'Grado V', nivel: 'INFERIOR', rango: '0 - 4' };
};

// Función para cargar y agregar el logo
const cargarYAgregarLogo = (doc, pageWidth) => {
    return new Promise((resolve) => {
        try {
            const logo = new Image();
            // Usar una ruta absoluta para evitar problemas
            logo.src = window.location.origin + '/logopartner.png';

            logo.onload = function () {
                // Calcular dimensiones manteniendo la proporción original
                const logoHeight = 18;
                const logoWidth = (logo.naturalWidth / logo.naturalHeight) * logoHeight;
                const logoX = (pageWidth - logoWidth) / 2;

                // Agregar imagen con proporción correcta
                doc.addImage(logo, 'JPEG', logoX, 11, logoWidth, logoHeight);
                resolve(true);
            };

            logo.onerror = function () {
                console.warn('No se pudo cargar el logo, usando fallback de texto');
                // Fallback texto
                doc.setTextColor(...hexToRgb(COLORES.NARANJA));
                doc.setFontSize(18);
                doc.setFont('helvetica', 'bold');
                doc.text('BPO PERU', pageWidth / 2, 20, { align: 'center' });
                resolve(false);
            };

            // Timeout por si la imagen tarda demasiado
            setTimeout(() => {
                if (!logo.complete) {
                    console.warn('Timeout cargando logo, usando fallback');
                    doc.setTextColor(...hexToRgb(COLORES.NARANJA));
                    doc.setFontSize(18);
                    doc.setFont('helvetica', 'bold');
                    doc.text('BPO PERU', pageWidth / 2, 20, { align: 'center' });
                    resolve(false);
                }
            }, 1000);

        } catch (error) {
            console.error('Error cargando logo:', error);
            // Fallback texto
            doc.setTextColor(...hexToRgb(COLORES.NARANJA));
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text('BPO PERU', pageWidth / 2, 20, { align: 'center' });
            resolve(false);
        }
    });
};

export const generarReportePDF = async (datosUsuario, datosExamen, resultado, returnDoc = false) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = 0;

    // Función para agregar header profesional
    const agregarHeader = async () => {
        // Fondo blanco
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageWidth, 35, 'F');

        // Barra superior naranja
        doc.setFillColor(...hexToRgb(COLORES.NARANJA));
        doc.rect(0, 0, pageWidth, 3, 'F');

        // Barra inferior verde
        doc.setFillColor(...hexToRgb(COLORES.VERDE));
        doc.rect(0, 32, pageWidth, 3, 'F');

        // Esperar a que el logo cargue y se agregue
        await cargarYAgregarLogo(doc, pageWidth);
    };

    // Footer profesional
    const agregarFooter = (pageNum) => {
        // Barra naranja superior
        doc.setFillColor(...hexToRgb(COLORES.NARANJA));
        doc.rect(0, pageHeight - 15, pageWidth, 2, 'F');

        // Fondo verde
        doc.setFillColor(...hexToRgb(COLORES.VERDE));
        doc.rect(0, pageHeight - 13, pageWidth, 13, 'F');

        // Texto del footer
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('PARTNER SERVICE', pageWidth / 2, pageHeight - 6, { align: 'center' });

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(`Pagina ${pageNum}`, pageWidth - margin, pageHeight - 6, { align: 'right' });
    };

    // Función para sección con título
    const agregarSeccionTitulo = (titulo, color, yPosition) => {
        doc.setFillColor(...hexToRgb(color));
        doc.roundedRect(margin, yPosition, contentWidth, 10, 1, 1, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(titulo, margin + 5, yPosition + 7);

        return yPosition + 10;
    };

    // ===== PÁGINA 1 =====
    await agregarHeader();
    yPos = 45;

    // Título principal del reporte
    doc.setFillColor(...hexToRgb(COLORES.NARANJA));
    doc.roundedRect(margin, yPos, contentWidth, 14, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('REPORTE INTEGRADO DE EVALUACION', pageWidth / 2, yPos + 9, { align: 'center' });

    yPos += 18;

    // Subtítulo descriptivo
    doc.setFillColor(...hexToRgb(COLORES.GRIS_CLARO));
    doc.roundedRect(margin, yPos, contentWidth, 15, 2, 2, 'F');
    doc.setTextColor(...hexToRgb(COLORES.TEXTO_SUAVE));
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const textoDesc = 'Brinda como resultado la clasificacion del evaluado en relacion a una bateria\nde pruebas previamente seleccionada.';
    doc.text(textoDesc, pageWidth / 2, yPos + 6, { align: 'center' });

    yPos += 20;

    // Información del candidato
    yPos = agregarSeccionTitulo('INFORMACION DEL CANDIDATO', COLORES.VERDE, yPos);
    yPos += 2;

    doc.setFillColor(...hexToRgb(COLORES.GRIS_CLARO));
    doc.roundedRect(margin, yPos, contentWidth, 28, 2, 2, 'F');

    yPos += 8;

    // Nombre destacado
    doc.setTextColor(...hexToRgb(COLORES.VERDE));
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(datosUsuario.nombre || 'N/A', margin + 5, yPos);

    yPos += 8;

    // Datos en dos columnas
    doc.setTextColor(...hexToRgb(COLORES.TEXTO));
    doc.setFontSize(10);

    doc.setFont('helvetica', 'bold');
    doc.text('DNI:', margin + 5, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(datosUsuario.dni || 'N/A', margin + 25, yPos);

    doc.setFont('helvetica', 'bold');
    doc.text('Proceso:', margin + 5, yPos + 7);
    doc.setFont('helvetica', 'normal');
    const procesoTexto = doc.splitTextToSize(datosUsuario.proceso || 'N/A', contentWidth - 35);
    doc.text(procesoTexto, margin + 25, yPos + 7);

    yPos += 20;

    // Información del Proceso
    yPos = agregarSeccionTitulo('INFORMACION DEL PROCESO', COLORES.VERDE, yPos);
    yPos += 5;

    // Calcular duración si tenemos fechas raw de inicio y fin
    let duracionCalculada = datosUsuario.duracion || 'N/A';
    if (duracionCalculada === 'N/A' && datosUsuario.fechaInicioRaw && datosUsuario.fechaFinRaw) {
        try {
            const inicio = new Date(datosUsuario.fechaInicioRaw);
            const fin = new Date(datosUsuario.fechaFinRaw);
            const diffMs = fin - inicio;
            
            if (diffMs > 0 && !isNaN(diffMs)) {
                const diffMinutos = Math.floor(diffMs / 60000);
                const diffSegundos = Math.floor((diffMs % 60000) / 1000);
                
                if (diffMinutos >= 60) {
                    const horas = Math.floor(diffMinutos / 60);
                    const mins = diffMinutos % 60;
                    duracionCalculada = `${horas}h ${mins}m ${diffSegundos}s`;
                } else if (diffMinutos > 0) {
                    duracionCalculada = `${diffMinutos} min ${diffSegundos} seg`;
                } else {
                    duracionCalculada = `${diffSegundos} segundos`;
                }
            }
        } catch (e) {
            console.error('Error calculando duración:', e);
        }
    }

    autoTable(doc, {
        startY: yPos,
        body: [
            ['Fecha de Inicio', datosUsuario.fechaInicio || 'N/A'],
            ['Fecha de Finalizacion', datosUsuario.fechaFin || 'N/A'],
            ['Duracion Total', duracionCalculada]
        ],
        bodyStyles: {
            fontSize: 10,
            cellPadding: 5
        },
        columnStyles: {
            0: {
                cellWidth: 80,
                fillColor: hexToRgb(COLORES.VERDE),
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                halign: 'left'
            },
            1: {
                fillColor: [255, 255, 255],
                textColor: hexToRgb(COLORES.TEXTO),
                halign: 'left',
                fontStyle: 'bold'
            }
        },
        margin: { left: margin, right: margin },
        theme: 'grid'
    });

    // Actualizar yPos tras la tabla de proceso
    yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : yPos + 12;

    // Forzar que 'RESULTADOS DE EVALUACION' comience en la página 2
    agregarFooter(1);
    doc.addPage();
    await agregarHeader();
    yPos = 45;

    // Sección de Resultados (comenzando en página nueva)
    yPos = agregarSeccionTitulo('RESULTADOS DE EVALUACION', COLORES.NARANJA, yPos);
    yPos += 5;

    // Tabla del test evaluado
    autoTable(doc, {
        startY: yPos,
        head: [['Test Aplicado', datosExamen.nombre || 'N/A']],
        headStyles: {
            fillColor: hexToRgb(COLORES.VERDE),
            textColor: [255, 255, 255],
            fontSize: 11,
            fontStyle: 'bold',
            halign: 'center',
            cellPadding: 6
        },
        columnStyles: {
            0: {
                cellWidth: 60,
                fillColor: hexToRgb(COLORES.VERDE),
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                halign: 'center'
            },
            1: {
                fillColor: [255, 255, 255],
                halign: 'center',
                fontStyle: 'bold',
                textColor: hexToRgb(COLORES.TEXTO)
            }
        },
        margin: { left: margin, right: margin },
        theme: 'grid'
    });

    yPos = doc.lastAutoTable.finalY + 8;

    // Tabla de métricas
    const respuestasCorrectas = resultado.respuestasCorrectas || 0;
    const respuestasIncorrectas = resultado.respuestasIncorrectas || 0;
    const totalPreguntas = resultado.totalPreguntas || (respuestasCorrectas + respuestasIncorrectas);
    const porcentaje = resultado.porcentajeAcierto || 0;

    autoTable(doc, {
        startY: yPos,
        head: [['METRICA', 'VALOR']],
        body: [
            ['Respuestas Correctas', `${respuestasCorrectas} / ${totalPreguntas}`],
            ['Respuestas Incorrectas', `${respuestasIncorrectas} / ${totalPreguntas}`],
            ['Porcentaje de Acierto', `${porcentaje.toFixed(1)}%`],
            ['Puntos Obtenidos', `${resultado.puntosObtenidos || 0} pts`]
        ],
        headStyles: {
            fillColor: hexToRgb(COLORES.VERDE),
            textColor: [255, 255, 255],
            fontSize: 11,
            fontStyle: 'bold',
            halign: 'center',
            cellPadding: 5
        },
        bodyStyles: {
            halign: 'center',
            fontSize: 10,
            cellPadding: 5
        },
        columnStyles: {
            0: {
                cellWidth: 100,
                fillColor: hexToRgb(COLORES.GRIS_CLARO),
                fontStyle: 'bold',
                textColor: hexToRgb(COLORES.TEXTO)
            },
            1: {
                cellWidth: 80,
                fillColor: [255, 255, 255],
                textColor: hexToRgb(COLORES.AZUL),
                fontStyle: 'bold'
            }
        },
        margin: { left: margin, right: margin },
        theme: 'grid'
    });

    yPos = doc.lastAutoTable.finalY + 12;

    // Verificar espacio para tabla de clasificación
    if (yPos > pageHeight - 120) {
        agregarFooter(1);
        doc.addPage();
        await agregarHeader();
        yPos = 45;
    }

    // Tabla de Clasificación
    yPos = agregarSeccionTitulo('TABLA DE CLASIFICACION', COLORES.NARANJA, yPos);
    yPos += 5;

    const gradoActual = obtenerGradoNivel(porcentaje);

    const clasificacionData = [
        ['Grado I', 'Intelectualmente Superior', '95 +', '#9333ea'],
        ['Grado II', 'Claramente por encima de la media', '75 - 94', '#3b82f6'],
        ['Grado III', 'Medio Superior', '50 - 74', '#06b6d4'],
        ['Grado III', 'Medio Inferior', '25 - 49', '#f59e0b'],
        ['Grado IV', 'Deficientemente inferior a la media', '5 - 24', '#f97316'],
        ['Grado V', 'Deficiente Intelectual', '0 - 4', '#ef4444']
    ];

    autoTable(doc, {
        startY: yPos,
        head: [['GRADO', 'CATEGORIA', 'PERCENTIL']],
        body: clasificacionData.map(row => {
            const esActual = row[2] === gradoActual.rango;
            return [
                row[0],
                row[1],
                esActual ? `${row[2]} <` : row[2]
            ];
        }),
        headStyles: {
            fillColor: hexToRgb(COLORES.VERDE),
            textColor: [255, 255, 255],
            fontSize: 10,
            fontStyle: 'bold',
            halign: 'center',
            cellPadding: 5
        },
        bodyStyles: {
            fontSize: 9,
            halign: 'center',
            cellPadding: 5
        },
        columnStyles: {
            0: {
                cellWidth: 30,
                fontStyle: 'bold',
                textColor: hexToRgb(COLORES.TEXTO)
            },
            1: {
                cellWidth: 105,
                textColor: hexToRgb(COLORES.TEXTO)
            },
            2: {
                cellWidth: 45,
                fontStyle: 'bold'
            }
        },
        didParseCell: function (data) {
            if (data.section === 'body') {
                const rowData = clasificacionData[data.row.index];
                const esActual = rowData[2] === gradoActual.rango;

                if (esActual) {
                    const color = hexToRgb(rowData[3]);
                    data.cell.styles.fillColor = [245, 247, 250];
                    data.cell.styles.textColor = color;
                    data.cell.styles.fontStyle = 'bold';
                }
            }
        },
        margin: { left: margin, right: margin },
        theme: 'grid'
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // Nota sobre centiles
    doc.setFillColor(...hexToRgb('#e0f2fe'));
    doc.roundedRect(margin, yPos, contentWidth, 14, 2, 2, 'F');

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...hexToRgb(COLORES.AZUL));
    doc.text('Centiles:', margin + 5, yPos + 6);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...hexToRgb(COLORES.TEXTO_SUAVE));
    doc.text('Medida comparativa utilizada en la tabla para realizar la calificacion del evaluado.', margin + 5, yPos + 11);

    yPos += 18;

    // Verificar espacio para interpretación
    if (yPos > pageHeight - 90) {
        agregarFooter(1);
        doc.addPage();
        await agregarHeader();
        yPos = 45;
    }

    // Interpretación del nivel
    const nivelRendimiento = resultado.nivelRendimiento || 'INFERIOR';
    const colorNivel = obtenerColorNivel(nivelRendimiento);
    const textoNivel = obtenerTextoNivel(nivelRendimiento);

    yPos = agregarSeccionTitulo('INTERPRETACION DE RESULTADOS', colorNivel, yPos);
    yPos += 5;

    // Banner del nivel
    doc.setFillColor(...hexToRgb(colorNivel));
    doc.roundedRect(margin, yPos, contentWidth, 12, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`NIVEL: ${textoNivel.toUpperCase()}`, pageWidth / 2, yPos + 8, { align: 'center' });

    yPos += 14;

    // Subcaja con grado y percentil
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...hexToRgb(colorNivel));
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, yPos, contentWidth, 9, 1, 1, 'FD');
    doc.setTextColor(...hexToRgb(colorNivel));
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`${gradoActual.grado} - Percentil: ${porcentaje.toFixed(1)}%`, pageWidth / 2, yPos + 6, { align: 'center' });

    yPos += 12;

    // Descripción del nivel
    doc.setFillColor(...hexToRgb(COLORES.GRIS_CLARO));
    doc.roundedRect(margin, yPos, contentWidth, 52, 2, 2, 'F');

    yPos += 6;

    doc.setTextColor(...hexToRgb(COLORES.TEXTO));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const descripcion = DESCRIPCIONES_NIVEL[nivelRendimiento] || 'Descripcion no disponible.';
    const lineasDescripcion = doc.splitTextToSize(descripcion, contentWidth - 10);
    doc.text(lineasDescripcion, margin + 5, yPos);

    yPos += 56;

    // Footer final
    agregarFooter(1);

    // Si se solicita retornar el doc (para combinar PDFs), no guardar
    if (returnDoc) {
        return doc;
    }

    // Guardar PDF usando Blob para evitar refresh
    const nombreArchivo = `Reporte_${datosUsuario.nombre?.replace(/ /g, '_')}_${datosExamen.nombre?.replace(/ /g, '_')}.pdf`;
    const pdfBlob = doc.output('blob');
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nombreArchivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return doc;
};

// ===== FUNCIÓN PARA GENERAR REPORTE DISC =====
export const generarReporteDISC = async (datosTestDisc, datosUsuario = null, datosProceso = null, returnDoc = false) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = 0;

    // Usar datos del test si no se proveen datos del usuario
    const nombreUsuario = datosUsuario?.nombre || datosTestDisc.usuarioNombre || 'N/A';
    const dniUsuario = datosUsuario?.dni || datosTestDisc.usuarioDni || 'N/A';
    const proceso = datosProceso?.nombre || 'Proceso de Selección';

    // Función para agregar header profesional
    const agregarHeader = async () => {
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageWidth, 35, 'F');

        doc.setFillColor(...hexToRgb(COLORES.NARANJA));
        doc.rect(0, 0, pageWidth, 3, 'F');

        doc.setFillColor(...hexToRgb('#297373'));
        doc.rect(0, 32, pageWidth, 3, 'F');

        await cargarYAgregarLogo(doc, pageWidth);
    };

    // Footer profesional
    const agregarFooter = (pageNum) => {
        doc.setFillColor(...hexToRgb(COLORES.NARANJA));
        doc.rect(0, pageHeight - 15, pageWidth, 2, 'F');

        doc.setFillColor(...hexToRgb('#297373'));
        doc.rect(0, pageHeight - 13, pageWidth, 13, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('PARTNER SERVICE', pageWidth / 2, pageHeight - 6, { align: 'center' });

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(`Página ${pageNum}`, pageWidth - margin, pageHeight - 6, { align: 'right' });
    };

    // Función para sección con título
    const agregarSeccionTitulo = (titulo, color, yPosition) => {
        doc.setFillColor(...hexToRgb(color));
        doc.roundedRect(margin, yPosition, contentWidth, 10, 1, 1, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(titulo, margin + 5, yPosition + 7);

        return yPosition + 10;
    };

    // ===== PÁGINA 1 =====
    await agregarHeader();
    yPos = 45;

    // Título principal del reporte
    doc.setFillColor(...hexToRgb(COLORES.NARANJA));
    doc.roundedRect(margin, yPos, contentWidth, 14, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('REPORTE DE TEST DISC', pageWidth / 2, yPos + 9, { align: 'center' });

    yPos += 18;

    // Subtítulo descriptivo
    doc.setFillColor(...hexToRgb(COLORES.GRIS_CLARO));
    doc.roundedRect(margin, yPos, contentWidth, 15, 2, 2, 'F');
    doc.setTextColor(...hexToRgb(COLORES.TEXTO_SUAVE));
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const textoDesc = 'Evaluación de personalidad y comportamiento mediante el modelo DISC:\nDominancia, Influencia, Estabilidad y Cumplimiento';
    doc.text(textoDesc, pageWidth / 2, yPos + 6, { align: 'center' });

    yPos += 20;

    // Información del candidato
    yPos = agregarSeccionTitulo('INFORMACIÓN DEL CANDIDATO', COLORES.VERDE, yPos);
    yPos += 2;

    doc.setFillColor(...hexToRgb(COLORES.GRIS_CLARO));
    doc.roundedRect(margin, yPos, contentWidth, 28, 2, 2, 'F');

    yPos += 8;

    doc.setTextColor(...hexToRgb(COLORES.MORADO));
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(nombreUsuario, margin + 5, yPos);

    yPos += 8;

    doc.setTextColor(...hexToRgb(COLORES.TEXTO));
    doc.setFontSize(10);

    doc.setFont('helvetica', 'bold');
    doc.text('DNI:', margin + 5, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(dniUsuario, margin + 25, yPos);

    doc.setFont('helvetica', 'bold');
    doc.text('Proceso:', margin + 5, yPos + 7);
    doc.setFont('helvetica', 'normal');
    const procesoTexto = doc.splitTextToSize(proceso, contentWidth - 35);
    doc.text(procesoTexto, margin + 25, yPos + 7);

    yPos += 20;

    // Información del Test
    yPos = agregarSeccionTitulo('INFORMACIÓN DEL TEST', COLORES.VERDE, yPos);
    yPos += 5;

    const fechaRealizacion = datosTestDisc.fechaRealizacion
        ? new Date(datosTestDisc.fechaRealizacion).toLocaleDateString('es-PE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
        : 'N/A';

    autoTable(doc, {
        startY: yPos,
        body: [
            ['Tipo de Test', 'Test DISC (28 Preguntas)'],
            ['Fecha de Realización', fechaRealizacion],
            ['Perfil Resultado', datosTestDisc.perfilResultado || 'N/A'],
            ['Segmento', `Segmento ${datosTestDisc.segmento || 'N/A'}`]
        ],
        bodyStyles: {
            fontSize: 10,
            cellPadding: 5
        },
        columnStyles: {
            0: {
                cellWidth: 80,
                fillColor: hexToRgb(COLORES.NARANJA),
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                halign: 'left'
            },
            1: {
                fillColor: [255, 255, 255],
                textColor: hexToRgb(COLORES.TEXTO),
                halign: 'left',
                fontStyle: 'bold'
            }
        },
        margin: { left: margin, right: margin },
        theme: 'grid'
    });

    yPos = doc.lastAutoTable.finalY + 12;

    // Nueva página para puntuaciones
    agregarFooter(1);
    doc.addPage();
    await agregarHeader();
    yPos = 45;

    // Sección de Puntuaciones DISC
    yPos = agregarSeccionTitulo('PUNTUACIONES DISC', '#297373', yPos);
    yPos += 8;

    // Gráfico visual de puntuaciones
    const puntuaciones = [
        { letra: 'D', nombre: 'Dominancia', valor: datosTestDisc.puntuacionD || 0, color: '#ef4444' },
        { letra: 'I', nombre: 'Influencia', valor: datosTestDisc.puntuacionI || 0, color: '#f59e0b' },
        { letra: 'S', nombre: 'Estabilidad', valor: datosTestDisc.puntuacionS || 0, color: '#10b981' },
        { letra: 'C', nombre: 'Cumplimiento', valor: datosTestDisc.puntuacionC || 0, color: '#3b82f6' }
    ];

    // Dibujar barras para cada puntuación
    puntuaciones.forEach((punt, index) => {
        const barY = yPos + (index * 20);
        const maxBarWidth = 120;
        const barHeight = 12;

        // Calcular ancho de barra (escala de -10 a +10)
        const normalizedValue = ((punt.valor + 10) / 20) * maxBarWidth;
        const barWidth = Math.max(5, normalizedValue);

        // Fondo de la barra
        doc.setFillColor(240, 240, 240);
        doc.roundedRect(margin + 45, barY, maxBarWidth, barHeight, 1, 1, 'F');

        // Barra de color
        doc.setFillColor(...hexToRgb(punt.color));
        doc.roundedRect(margin + 45, barY, barWidth, barHeight, 1, 1, 'F');

        // Etiqueta
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...hexToRgb(punt.color));
        doc.text(punt.letra, margin + 5, barY + 8);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...hexToRgb(COLORES.TEXTO));
        doc.text(punt.nombre, margin + 12, barY + 8);

        // Valor
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...hexToRgb(punt.color));
        doc.text(`${punt.valor >= 0 ? '+' : ''}${punt.valor}`, margin + 170, barY + 8);
    });

    yPos += 85;

    // Perfil resultado destacado
    doc.setFillColor(...hexToRgb('#f3e8ff'));
    doc.roundedRect(margin, yPos, contentWidth, 18, 2, 2, 'F');

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...hexToRgb(COLORES.MORADO));
    doc.text('PERFIL IDENTIFICADO:', margin + 5, yPos + 7);

    doc.setFontSize(13);
    doc.setTextColor(...hexToRgb('#e91e63'));
    doc.text(datosTestDisc.perfilResultado || 'N/A', margin + 5, yPos + 14);

    yPos += 22;

    // Nueva página para análisis
    agregarFooter(2);
    doc.addPage();
    await agregarHeader();
    yPos = 45;

    // Análisis del Perfil
    yPos = agregarSeccionTitulo('ANÁLISIS DEL PERFIL', COLORES.VERDE, yPos);
    yPos += 5;

    const analisis = [
        { titulo: 'Descripción Completa', contenido: datosTestDisc.descripcionCompleta },
        { titulo: 'Emociones', contenido: datosTestDisc.descripcionEmociones },
        { titulo: 'Meta', contenido: datosTestDisc.descripcionMeta },
        { titulo: 'Juzga a otros por', contenido: datosTestDisc.descripcionJuzga },
        { titulo: 'Influye mediante', contenido: datosTestDisc.descripcionInfluye },
        { titulo: 'Su valor para la organización', contenido: datosTestDisc.descripcionValor },
        { titulo: 'Abusa de', contenido: datosTestDisc.descripcionAbusa }
    ];

    analisis.forEach((item, index) => {
        if (yPos > pageHeight - 40) {
            agregarFooter(3);
            doc.addPage();
            agregarHeader();
            yPos = 45;
        }

        // Configurar tamaño y fuente antes de splitTextToSize
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        
        // Caja para cada análisis - usar TODO el ancho disponible
        const anchoTexto = contentWidth - 10; // Máximo ancho posible
        const contenidoLineas = doc.splitTextToSize(item.contenido || 'N/A', anchoTexto);
        const alturaCaja = contenidoLineas.length * 4 + 10;

        doc.setFillColor(250, 250, 250);
        doc.setDrawColor(...hexToRgb(COLORES.MORADO));
        doc.setLineWidth(0.3);
        doc.roundedRect(margin, yPos, contentWidth, alturaCaja, 1, 1, 'FD');

        // Título
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...hexToRgb(COLORES.MORADO));
        doc.text(`${item.titulo}:`, margin + 5, yPos + 6);

        // Contenido - renderizar líneas con el ancho completo
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...hexToRgb(COLORES.TEXTO));
        
        contenidoLineas.forEach((linea, i) => {
            doc.text(linea, margin + 5, yPos + 11 + (i * 4));
        });

        yPos += alturaCaja + 3;
    });

    // Nueva página para conclusiones
    if (yPos > pageHeight - 80) {
        agregarFooter(3);
        doc.addPage();
        await agregarHeader();
        yPos = 45;
    }

    // Bajo presión y temores
    yPos = agregarSeccionTitulo('COMPORTAMIENTO Y TEMORES', '#297373', yPos);
    yPos += 5;

    const comportamiento = [
        { titulo: 'Bajo presión', contenido: datosTestDisc.descripcionPresion, color: '#f59e0b' },
        { titulo: 'Teme', contenido: datosTestDisc.descripcionTeme, color: '#ef4444' }
    ];

    comportamiento.forEach((item) => {
        const contenidoLineas = doc.splitTextToSize(item.contenido || 'N/A', contentWidth - 15);
        const alturaCaja = Math.max(12, contenidoLineas.length * 5 + 8);

        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(...hexToRgb(item.color));
        doc.setLineWidth(0.5);
        doc.roundedRect(margin, yPos, contentWidth, alturaCaja, 1, 1, 'FD');

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...hexToRgb(item.color));
        doc.text(item.titulo.toUpperCase(), margin + 5, yPos + 6);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...hexToRgb(COLORES.TEXTO));
        doc.text(contenidoLineas, margin + 5, yPos + 11);

        yPos += alturaCaja + 4;
    });

    // Recomendaciones
    yPos += 3;
    yPos = agregarSeccionTitulo('RECOMENDACIONES', '#10b981', yPos);
    yPos += 5;

    const eficacia = datosTestDisc.descripcionEficaz || 'N/A';
    const eficaciaLineas = doc.splitTextToSize(eficacia, contentWidth - 15);
    const alturaEficacia = Math.max(18, eficaciaLineas.length * 5 + 10);

    doc.setFillColor(...hexToRgb('#d1fae5'));
    doc.setDrawColor(...hexToRgb('#10b981'));
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, yPos, contentWidth, alturaEficacia, 2, 2, 'FD');

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...hexToRgb('#10b981'));
    doc.text('Sería más eficaz si:', margin + 5, yPos + 7);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...hexToRgb(COLORES.TEXTO));
    doc.text(eficaciaLineas, margin + 5, yPos + 13);

    // Footer final
    agregarFooter(4);

    // Si se solicita retornar el doc (para combinar PDFs), no guardar
    if (returnDoc) {
        return doc;
    }

    // Guardar PDF usando Blob para evitar refresh
    const nombreArchivo = `Reporte_DISC_${nombreUsuario.replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    const pdfBlob = doc.output('blob');
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nombreArchivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return doc;
};

// ===== FUNCIÓN PARA GENERAR REPORTE COMBINADO (RAVEN + DISC + IPV) =====
// Genera los 3 reportes COMPLETOS uno tras otro en un solo PDF
export const generarReporteCombinado = async (datosUsuario, datosRaven, datosDisc, ipvResultado) => {
    // Usaremos pdf-lib para combinar los PDFs
    const { PDFDocument } = await import('pdf-lib');
    
    const pdfsCombinar = [];

    // Generar PDF Raven COMPLETO si tiene datos (usando la función original)
    if (datosRaven) {
        const datosExamen = { nombre: 'Test de Raven - Matrices Progresivas' };
        const resultado = {
            respuestasCorrectas: datosRaven.puntaje,
            respuestasIncorrectas: datosRaven.puntajeMax - datosRaven.puntaje,
            totalPreguntas: datosRaven.puntajeMax,
            porcentajeAcierto: datosRaven.porcentaje,
            puntosObtenidos: datosRaven.puntaje,
            nivelRendimiento: datosRaven.nivel === 'Normal Superior' ? 'NORMAL_SUPERIOR' : 
                             datosRaven.nivel === 'Normal Promedio' ? 'NORMAL_PROMEDIO' : 'INFERIOR'
        };
        
        // Usar la función ORIGINAL completa con returnDoc=true
        const docRaven = await generarReportePDF(datosUsuario, datosExamen, resultado, true);
        pdfsCombinar.push(docRaven.output('arraybuffer'));
    }

    // Generar PDF DISC COMPLETO si tiene datos (usando la función original)
    if (datosDisc) {
        // Usar la función ORIGINAL completa con returnDoc=true
        const docDisc = await generarReporteDISC(datosDisc, { nombre: datosUsuario.nombre, dni: datosUsuario.dni }, null, true);
        pdfsCombinar.push(docDisc.output('arraybuffer'));
    }

    // Generar PDF IPV COMPLETO si tiene datos (usando la función original)
    if (ipvResultado) {
        const { generarPDFIpv } = await import('./pdfGeneratorIpv');
        // Usar la función ORIGINAL completa con returnDoc=true
        const docIpv = await generarPDFIpv(ipvResultado, true);
        pdfsCombinar.push(docIpv.output('arraybuffer'));
    }

    if (pdfsCombinar.length === 0) {
        throw new Error('No hay PDFs para combinar');
    }

    // Combinar todos los PDFs usando pdf-lib
    const pdfCombinado = await PDFDocument.create();

    for (const pdfBytes of pdfsCombinar) {
        const pdfDoc = await PDFDocument.load(pdfBytes);
        const paginas = await pdfCombinado.copyPages(pdfDoc, pdfDoc.getPageIndices());
        paginas.forEach(pagina => pdfCombinado.addPage(pagina));
    }

    // Guardar el PDF combinado
    const pdfCombinadoBytes = await pdfCombinado.save();
    const blob = new Blob([pdfCombinadoBytes], { type: 'application/pdf' });
    const nombreArchivo = `Reporte_Integral_${datosUsuario.nombre?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nombreArchivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export default generarReportePDF;